import { off } from "process";

export interface Logger {
  debug(message?: unknown, ...optionalParams: unknown[]): void;
  log(message?: unknown, ...optionalParams: unknown[]): void;
  error(message?: unknown, ...optionalParams: unknown[]): void;
}

export interface RPCObserver {
  onStatus(message: string): void;
  onProgress(num: number, div: number): void;
}

export interface RPCRange {
  start: number;
  end: number;

  fragmentSize: number;

  type: RPCRangeType;
  description: string;
}

export interface RPCRangeInfo {

  version: number;
  fragmentSize: number;

  ranges: RPCRange[];
}

export enum RPCRangeType {
  UNKNOWN            = 0,
  FIRMWARE           = 1, // Main Firmware
  FIRMWARE_SECONDARY = 2, // Primary Bootloader
  STYLEDATA          = 4, // Styleplayer Data
  DEMODATA           = 6, // Demoplayer Data
  RHYTHMDATA         = 8, // Rhythmplayer Data

  SOUNDBANK          = 16, // Soundbank (NOR Flash)
  SOUNDBANK_NAND2k   = 17,
  SOUNDBANK_NAND4k   = 18,

  SOUNDBANK_REGION   = 24, // Managed Soundbank Region
}

enum RPC_PROTO_MESSAGE {
  USBRPCPROTO_UXUPDATE_FIRST              = 0x1000,
  USBRPCPROTO_UXUPDATE_GET_RANGES         = 0x1000,
  USBRPCPROTO_UXUPDATE_START_PROGRAMMING  = 0x1001,
  USBRPCPROTO_UXUPDATE_END_PROGRAMMING    = 0x1002,
  USBRPCPROTO_UXUPDATE_PROGRAM            = 0x1003,
  USBRPCPROTO_UXUPDATE_SYNC               = 0x1004,
  USBRPCPROTO_UXUPDATE_LAST               = 0x1008,
}

enum RPCStatus {
  OK,

  ERROR,

  NO_RESPONSE
}

interface RPCReply {
  status: RPCStatus;
  code?: number;
  data?: ArrayBufferLike;
}

export class DreamRPC {

  protected endpointIn: number = 0x04;
  protected endpointOut: number = 0x04;

  protected _device: USBDevice | undefined;

  constructor(
    protected logger: Logger, 
    protected observer: RPCObserver) {

      this._device = undefined;
  }

  async flashSoundbank(dataBuffer: ArrayBuffer) : Promise<boolean> {
    var success = await this.connect();

    return success;
  }

  async connect() : Promise<boolean> {

    // Filter only Dream USB
    const filters = [
      {
        vendorId: 0x2485,
        productId: 0x444d,
        classCode: 0xEF
      }
    ];

    try {
      this._device = await navigator.usb.requestDevice({ filters: filters });

      await this._device.open();
      await this._device.selectConfiguration(1);
      await this._device.claimInterface(0);

      return true;
    } 
    catch (e : any) {
      this._device = undefined;
      throw new Error("Connection failed: " + e.message);
    }
  }

  hasConnection() : boolean {
    return (this._device != undefined);
  }

  private composeMessage(
      message: RPC_PROTO_MESSAGE, 
      blockSize: number = 0, 
      blockCount: number = 0, 
      data: number[] | undefined = undefined) : ArrayBuffer {

    var additionalSize = (data?.length || 0) * 2;

    const buffer = new ArrayBuffer(16 + additionalSize);
    var intView = new Int16Array(buffer);

    for (var i = 0; i < intView.length; i++) {
      intView[ i ] = 0;
    }

    intView[0] = 0x5542;
    intView[1] = 0x7562;

    intView[2] = blockSize;
    intView[3] = blockCount;
    
    intView[4] = message;
    intView[5] = additionalSize / 2;

    if (data) {
      for (var i = 0; i < additionalSize; i++) {
        intView[8 + i] = data[i];
      }
    }

    return buffer;
  }

  public async requestRanges() : Promise<RPCRangeInfo> {

    // Ask for ranges in format version #1 (extended)
    var message = this.composeMessage(RPC_PROTO_MESSAGE.USBRPCPROTO_UXUPDATE_GET_RANGES, 0, 0, [ 1 ]);
    var bytes = await this.sendRequest(message);
    
    var response = await this.readResponse(50);

    if (response.status == RPCStatus.OK && response.data) {
      var buffer = response.data;
      var data = new Int16Array(buffer);

      var fragmentSize = data[0];
      var numRanges = data[1];
      var version = data[2];
      var reserved = data[3];
      
      var ranges = this.parseRanges(numRanges, version, fragmentSize * 2, buffer.slice(8));

      return {
        version: version,
        fragmentSize: fragmentSize,

        ranges: ranges
      }
    }

    return <RPCRangeInfo>{};
  }

  private parseRanges(numRanges: number, version: number, fragmentSize: number, buffer: ArrayBufferLike) {

    var data = new ArrayBuffer(buffer.byteLength);
    new Uint8Array(data).set(new Uint8Array(buffer));

    var intData = new Int16Array(data);

    var offset = 0;
    var result: RPCRange[] = [];

    for (var i = 0; i < numRanges; i++) {
      if (version == 1) {
          var pageBegin = intData[offset++] << 16;
          var pageEnd   = intData[offset++] << 16;
          var rangeType = intData[offset++];
          var reserved0 = intData[offset++];
          var reserved1 = intData[offset++];
          var reserved2 = intData[offset++];
          
          var chars: number[] = [];
          var done = false;

          for (var c = 0; c < 6; c++) {
            var word = intData[offset++];

            for (var ch of [  word & 0xff, (word & 0xff00) >> 8]) {
              done ||= (ch == 0);

              if (!done) {
                chars.push(ch);
              }
            }
          }
          var description = String.fromCharCode(...chars);

          result.push({
            start: pageBegin,
            end: pageEnd,
            type: rangeType,
            
            fragmentSize: fragmentSize,

            description: description
          });
      }
    }

    return result;
  }

  private async sendRequest(buffer: ArrayBuffer) : Promise<number> {

    if (!this._device) {
      throw new Error("No device connected.");
    }

    const result = await this._device.transferOut(this.endpointOut, buffer);
    return result.bytesWritten;
  }

  public async programRange(range: RPCRange, data: Uint8Array) {
    return await this.doProgramRange(range, <ArrayBuffer>(data.buffer))
  }

  private message(msg: string) {
    this.observer.onStatus(msg);
  }

  private progress(num: number, div: number) {
    this.observer.onProgress(num, div);
  }

  private async doProgramRange(range: RPCRange, data: ArrayBuffer) {

    await this.beginProgrammingRange(range);

    var total = data.byteLength;
    var blocks = Math.ceil(data.byteLength / range.fragmentSize); 

    var batchSize = 32;
    var offset = 0;
    var bytes = total;

    this.progress(0, total);

    while (blocks > 0) {
      var blockCount = Math.min(blocks, batchSize);

      await this.startProgrammingBlock(range.fragmentSize, blockCount);

      while (blockCount > 0) {
        var sliceLen = Math.min(range.fragmentSize, bytes);
        var nextOffset = offset + sliceLen;

        var block =  data.slice(offset, nextOffset);  
        if (sliceLen < range.fragmentSize) {
          block = (<any>block).transferToFixedLength(range.fragmentSize);
        }
        
        var written = await this.programBlock(block);
        
        offset = nextOffset
        bytes -= sliceLen;
        blockCount--;
      
        this.progress(total - bytes, total);
  
        blocks--;
      }
    }

    await this.endProgramming();

    await this.sync();
  }

  async programBlock(block: ArrayBuffer) {
    if (this._device == null) {
      throw new Error("No device connected.");
    }

    await this._device.transferOut(this.endpointOut, block);
  }

  async beginProgrammingRange(range: RPCRange) {
    var message = this.composeMessage(RPC_PROTO_MESSAGE.USBRPCPROTO_UXUPDATE_START_PROGRAMMING, 0, 0, [ range.start & 0xffff, (range.start  & 0xffff0000) >> 16]);
    await this.sendRequest(message);
  }

  async startProgrammingBlock(fragmentSize: number, fragmentCount: number) {
    var message = this.composeMessage(RPC_PROTO_MESSAGE.USBRPCPROTO_UXUPDATE_PROGRAM, fragmentSize, fragmentCount);
    await this.sendRequest(message);
  }

  async endProgramming() {
    var message = this.composeMessage(RPC_PROTO_MESSAGE.USBRPCPROTO_UXUPDATE_END_PROGRAMMING);
    await this.sendRequest(message);
  }

  async sync() {
    var message = this.composeMessage(RPC_PROTO_MESSAGE.USBRPCPROTO_UXUPDATE_SYNC);
    await this.sendRequest(message);
  }

  async readResponse(expectedWords: number = 5) : Promise<RPCReply> {
    if (!this._device) {
      throw new Error("No device connected.");
    }

    const result = await this._device.transferIn(this.endpointIn, expectedWords * 2);

    return this.parseReply(result.data?.buffer);
  }

  private parseReply(buffer: ArrayBufferLike | undefined) : RPCReply {

    if (buffer == undefined) {
      return { status : RPCStatus.NO_RESPONSE }
    }

    var intArray = new Int16Array(buffer);

    if (intArray[0] != 0x5542 || intArray[1] != 0x7453) {
      throw new Error("Response has wrong package signature.");
    }

    if (intArray[2] != 1) {
      throw new Error("Wrong protocol flow indicated.");
    }

    var status = intArray[3];
    var length = intArray[4] * 2 + 10;

    return {
      status: (status == 1 ? RPCStatus.OK : RPCStatus.ERROR),

      code: status,
      data: buffer.slice(10)
    }
  }
}